import React, { useEffect, useState } from "react";
import { Check, X, AlertTriangle, ThumbsUp } from "lucide-react";
import { TRASPARENZA_AREE, fasciaTrasparenza } from "../trasparenzaSchema";

const barColor = (ratio) => (ratio >= 0.7 ? "#2e7d32" : ratio >= 0.4 ? "#f5a623" : "#d32f2f");

const formatData = (iso) => {
  if (!iso) return "";
  const p = iso.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

const TrasparenzaCard = ({ trasparenza }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  if (!trasparenza) return null;
  const total = trasparenza.punteggio_totale || 0;
  const fascia = fasciaTrasparenza(total);
  const aree = trasparenza.aree || {};

  return (
    <div className="border-2 border-[#1f4680] rounded-xl overflow-hidden mt-8">
      <div className="bg-[#1f4680] text-white px-5 md:px-6 py-4">
        <h3 className="font-serif-display text-xl flex items-center gap-2">🔍 Indice di Trasparenza EGM</h3>
      </div>

      <div className="p-5 md:p-6">
        {/* Score */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Punteggio complessivo</span>
          <span className="text-sm font-bold" style={{ color: fascia.color }}>{fascia.stars} {fascia.label}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: mounted ? `${total}%` : "0%", background: "#f5a623" }}
            />
          </div>
          <span className="text-2xl font-extrabold text-sq-navy w-24 text-right">{total}/100</span>
        </div>

        {/* Areas */}
        <div className="mt-6 space-y-4">
          {TRASPARENZA_AREE.map((area) => {
            const a = aree[area.key] || { punteggio: 0, max: area.max, criteri: {} };
            const ratio = area.max ? a.punteggio / area.max : 0;
            return (
              <div key={area.key} className="border border-sq rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sq-navy text-sm">{area.label}</span>
                  <span className="text-sm font-bold" style={{ color: barColor(ratio) }}>{a.punteggio}/{area.max}</span>
                </div>
                <div className="bg-gray-100 rounded-full h-2.5 overflow-hidden mb-3">
                  <div className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{ width: mounted ? `${ratio * 100}%` : "0%", background: barColor(ratio) }} />
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {area.criteri.map((c) => {
                    const crit = (a.criteri || {})[c.key] || { valore: false, punti: 0, max: c.max };
                    const ok = crit.valore && crit.punti > 0;
                    const partial = crit.punti > 0 && crit.punti < crit.max;
                    return (
                      <li key={c.key} className="flex items-center gap-2 text-sm">
                        {ok ? (
                          <Check size={15} className="text-emerald-600 flex-shrink-0" />
                        ) : (
                          <X size={15} className="text-red-500 flex-shrink-0" />
                        )}
                        <span className={ok ? "text-gray-700" : "text-gray-400"}>{c.label}</span>
                        <span className="text-xs text-gray-400 ml-auto">{crit.punti}/{c.max}{partial ? " ▪" : ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Cosa manca */}
        {(trasparenza.cosa_manca || []).length > 0 && (
          <div className="mt-6 rounded-lg p-4" style={{ background: "#fff6ec", border: "1px solid #f7dcbd" }}>
            <div className="flex items-center gap-2 font-semibold text-[#b7791f] mb-2">
              <AlertTriangle size={16} /> Cosa manca per migliorare
            </div>
            <ul className="space-y-1.5">
              {trasparenza.cosa_manca.map((m, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span>⚠️</span><span>{m}</span></li>
              ))}
            </ul>
          </div>
        )}

        {/* Punti di forza */}
        {(trasparenza.punti_di_forza_trasparenza || []).length > 0 && (
          <div className="mt-4 rounded-lg p-4" style={{ background: "#e8f5e9", border: "1px solid #cfe8d1" }}>
            <div className="flex items-center gap-2 font-semibold text-emerald-700 mb-2">
              <ThumbsUp size={16} /> Punti di forza
            </div>
            <ul className="space-y-1.5">
              {trasparenza.punti_di_forza_trasparenza.map((p, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span>✅</span><span>{p}</span></li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-5 md:px-6 py-3 border-t border-sq text-xs text-gray-500 text-center">
        Valutazione indipendente di societaquotate.it — aggiornata al {formatData(trasparenza.data_rilevazione) || "—"}
      </div>
    </div>
  );
};

export default TrasparenzaCard;
